import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Trophy, 
  Users, 
  Settings, 
  BarChart3, 
  Smartphone, 
  Gift, 
  Shield, 
  Clock,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  CheckCircle,
  ArrowRight,
  Mail,
  Phone,
  MapPin,
  Award,
  Target,
  TrendingUp,
  Menu,
  X,
  Store,
  Coffee,
  Car,
  Scissors,
  Utensils,
  Heart,
  Briefcase,
  ShoppingBag,
  Grape,
  UserPlus,
  ShoppingCart,
  Star,
  Share2,
  Repeat,
  ArrowDown,
  Zap,
  BarChart,
  Cog,
  Sparkles,
  Play
} from 'lucide-react';

const Home = () => {
  // Carousel state
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Carousel slides data
  const carouselSlides = [
    {
      title: "Sistema de Fidelidade de Clientes",
      subtitle: "MeuFiel",
      description: "A plataforma completa de fidelização que transforma visitantes em clientes fiéis. Programa de pontos profissional que aumenta suas vendas, melhora a retenção e cria relacionamentos duradouros com seus clientes.",
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80",
      highlight: {
        icon: <Zap className="w-5 h-5" />,
        text: "Software de fidelização com ciclo completo de retenção de clientes"
      },
      buttons: [
        { text: "Teste 5 dias Grátis", link: "/trial-registration", primary: true },
        { text: "Área do Cliente", link: "/customer-auth", primary: false }
      ]
    },
    {
      title: "Programa de Pontos",
      subtitle: "Inteligente",
      description: "Sistema CRM completo para gestão de clientes e programa de fidelidade. Cadastre, organize e acompanhe seus clientes com histórico completo de compras, pontos e resgates em um só lugar.",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2426&q=80",
      highlight: {
        icon: <BarChart className="w-5 h-5" />,
        text: "Dashboard completo com relatórios de fidelização em tempo real"
      },
      buttons: [
        { text: "Ver Demonstração", link: "https://www.youtube.com/watch?v=RXR8J0tDw3I", primary: true, external: true }
      ]
    },
    {
      title: "Software de Fidelização",
      subtitle: "Personalizado",
      description: "Configure seu programa de fidelidade único. Sistema de pontos flexível com regras personalizadas, bônus especiais e campanhas promocionais exclusivas para qualquer tipo de negócio.",
      image: "https://images.unsplash.com/photo-1553729459-efe14ef6055d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80",
      highlight: {
        icon: <Cog className="w-5 h-5" />,
        text: "Sistema de recompensas configurável para qualquer segmento"
      },
      buttons: [
        { text: "Conhecer Recursos", link: "/trial-registration", primary: true }
      ]
    },
    {
      title: "Cartão Fidelidade",
      subtitle: "Digital",
      description: "Crie um catálogo irresistível de prêmios e recompensas. Sistema completo de gestão de estoque, configuração de preços em pontos e acompanhamento automático de resgates.",
      image: "https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80",
      highlight: {
        icon: <Sparkles className="w-5 h-5" />,
        text: "Programa de recompensas com catálogo ilimitado de prêmios"
      },
      buttons: [
        { text: "Explorar Catálogo", link: "/trial-registration", primary: true }
      ]
    }
  ];

  // Pause carousel on hover
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const header = event.target.closest('header');
      const mobileMenuButton = event.target.closest('button');
      
      if (isMobileMenuOpen && !header && !mobileMenuButton) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('click', handleClickOutside);
    }
    
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (isCarouselPaused) return;
    
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [carouselSlides.length, isCarouselPaused]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselSlides.length) % carouselSlides.length);
  };

  const features = [
    {
      icon: <Users className="w-8 h-8" />,
      title: "Gestão de Clientes",
      description: "Cadastre e gerencie clientes de forma simples e organizada. Mantenha histórico completo de cada cliente."
    },
    {
      icon: <Trophy className="w-8 h-8" />,
      title: "Sistema de Pontos",
      description: "Configure regras personalizadas de pontuação. Defina quantos pontos por real gasto e bônus de boas-vindas."
    },
    {
      icon: <Gift className="w-8 h-8" />,
      title: "Catálogo de Prêmios",
      description: "Crie um catálogo atrativo de recompensas. Gerencie prêmios e controle de estoque automaticamente."
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Relatórios Avançados",
      description: "Acompanhe métricas detalhadas do seu programa de fidelidade. Dashboard com insights em tempo real."
    },
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: "Notificações SMS",
      description: "Envie SMS automáticos para clientes quando ganharem ou resgatarem pontos. Mantenha-os sempre engajados."
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Expiração de Pontos",
      description: "Configure prazos de validade para pontos. Sistema automático de expiração personalizável por empresa."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Segurança Total",
      description: "Plataforma segura com autenticação robusta. Seus dados e dos clientes sempre protegidos."
    },
    {
      icon: <Settings className="w-8 h-8" />,
      title: "Personalização",
      description: "Personalize cores, logo e identidade visual. Adapte o sistema à sua marca e necessidades."
    }
  ];

  const benefits = [
    {
      icon: <Target className="w-6 h-6" />,
      title: "Aumente a Fidelização",
      description: "Clientes fiéis compram 90% mais frequentemente"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Incremente as Vendas",
      description: "Programas de fidelidade aumentam vendas em até 25%"
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Melhore a Experiência",
      description: "Clientes satisfeitos recomendam sua empresa"
    }
  ];

  // Target audience data
  const targetAudience = [
    {
      title: "Restaurantes e Lanchonetes",
      description: "Fidelize clientes habituais e aumente o ticket médio com pontos por valor gasto e prêmios atrativos.",
      image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80",
      icon: <Utensils className="w-8 h-8" />,
      benefits: ["Pontos por valor gasto", "Prêmios gastronômicos", "SMS automático", "Controle de estoque"]
    },
    {
      title: "Barbearias e Salões de Beleza",
      description: "Mantenha seus clientes sempre voltando com programas de pontos por serviços e produtos de beleza e cuidados masculinos.",
      image: "https://images.unsplash.com/photo-1560066984-138dadb4c035?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80",
      icon: <Scissors className="w-8 h-8" />,
      benefits: ["Pontos por serviço", "Desconto em tratamentos", "Lembretes automáticos", "Histórico completo"]
    },
    {
      title: "Lojas de Roupas",
      description: "Transforme compras esporádicas em relacionamentos duradouros com seu programa de fidelidade personalizado.",
      image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80",
      icon: <ShoppingBag className="w-8 h-8" />,
      benefits: ["Pontos por compra", "Desconto progressivo", "Lançamentos exclusivos", "Programa VIP"]
    },
    {
      title: "Cafeterias",
      description: "Conquiste clientes matinais e vespertinos com recompensas que os façam voltar todos os dias.",
      image: "https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80",
      icon: <Coffee className="w-8 h-8" />,
      benefits: ["Café grátis a cada X", "Pontos por visita", "Horário de pico", "Programa matinal"]
    },
    {
      title: "Açaíterias",
      description: "Fidelize clientes que procuram alimentação saudável com pontos por compras e recompensas especiais.",
      image: "https://images.unsplash.com/photo-1571068316344-75bc76f77890?auto=format&fit=crop&w=1200&q=80",
      icon: <Grape className="w-8 h-8" />,
      benefits: ["Pontos por compra", "Desconto progressivo", "Combo especiais", "Programa de frequência"]
    },
    {
      title: "Clínicas e Petshops",
      description: "Fidelize clientes e seus pets com programas de pontos em saúde, produtos e serviços veterinários.",
      image: "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80",
      icon: <Heart className="w-8 h-8" />,
      benefits: ["Pontos por consulta", "Desconto em produtos", "Lembretes de vacinas", "Programa preventivo"]
    }
  ];

  // How it works steps
  const howItWorksSteps = [
    {
      step: 1,
      title: "Cliente se Cadastra no Programa",
      description: "O cliente se inscreve no programa de fidelidade fornecendo dados básicos como nome, telefone e e-mail no seu estabelecimento ou através do sistema online.",
      icon: <UserPlus className="w-8 h-8" />,
      color: "bg-blue-500",
      details: ["Cadastro rápido em programa de fidelidade", "Dados seguros e protegidos", "Validação automática por SMS"]
    },
    {
      step: 2,
      title: "Cliente Faz Compras e Ganha Pontos",
      description: "A cada compra no programa de pontos, o cliente informa seu telefone ou é identificado no sistema e ganha pontos automaticamente conforme regras de fidelização.",
      icon: <ShoppingCart className="w-8 h-8" />,
      color: "bg-green-500",
      details: ["Pontos por valor gasto no sistema", "Identificação rápida por telefone", "Histórico automático de compras"]
    },
    {
      step: 3,
      title: "Cliente Acumula Pontos de Fidelidade",
      description: "Os pontos do programa de recompensas são creditados automaticamente na conta do cliente, que recebe notificação por SMS sobre o saldo de pontos.",
      icon: <Star className="w-8 h-8" />,
      color: "bg-yellow-500",
      details: ["Notificação automática por SMS", "Saldo de pontos sempre atualizado", "Pontos nunca se perdem*"]
    },
    {
      step: 4,
      title: "Cliente Resgata Prêmios do Catálogo",
      description: "Com pontos suficientes no programa de fidelidade, o cliente escolhe prêmios do catálogo de recompensas e faz o resgate diretamente no estabelecimento.",
      icon: <Gift className="w-8 h-8" />,
      color: "bg-purple-500",
      details: ["Catálogo de prêmios sempre atualizado", "Resgate instantâneo de recompensas", "Controle automático de estoque"]
    },
    {
      step: 5,
      title: "Cliente Indica Amigos para o Programa",
      description: "Clientes satisfeitos com o sistema de fidelidade recebem pontos bônus por cada indicação e se tornam promotores naturais da sua marca.",
      icon: <Share2 className="w-8 h-8" />,
      color: "bg-indigo-500",
      details: ["Bônus por indicação no programa", "Marketing boca a boca automático", "Crescimento orgânico de clientes"]
    },
    {
      step: 6,
      title: "Cliente Retorna Mais Vezes",
      description: "O ciclo de fidelização se reinicia! Cliente satisfeito com o programa de pontos volta mais vezes, gasta mais e traz novos clientes para seu negócio.",
      icon: <Repeat className="w-8 h-8" />,
      color: "bg-pink-500",
      details: ["Maior frequência de visitas", "Aumento do ticket médio", "Fidelização comprovada do cliente"]
    }
  ];

  const plans = [
    {
      name: "Básico",
      price: "R$ 79,90",
      period: "/mês",
      originalPrice: "R$ 99,90",
      periodType: "semestral",
      description: "Perfeito para pequenos negócios",
      features: [
        "Até 200 clientes",
        "Sistema de pontos básico",
        "5 prêmios no catálogo",
        "Relatórios básicos",
        "Suporte por email"
      ],
      highlighted: false
    },
    {
      name: "Profissional",
      price: "R$ 99,90",
      period: "/mês",
      originalPrice: "R$ 139,90",
      periodType: "semestral",
      description: "Ideal para empresas em crescimento",
      features: [
        "Até 500 clientes",
        "Sistema completo de pontos",
        "Prêmios ilimitados",
        "Notificações SMS",
        "Relatórios avançados",
        "Personalização visual",
        "Suporte prioritário"
      ],
      highlighted: true
    },
    {
      name: "Enterprise",
      price: "R$ 139,90",
      period: "/mês",
      originalPrice: "R$ 189,90",
      periodType: "semestral",
      description: "Para grandes empresas",
      features: [
        "Clientes ilimitados",
        "Todas as funcionalidades",
        "Múltiplas filiais",
        "ChatBot integrado",
        "API personalizada",
        "Suporte 24/7",
        "Gerente de conta dedicado"
      ],
      highlighted: false
    },    
    {
      name: "Business",
      price: "à consultar",
      period: "/mês",
      description: "Para empresas que buscam soluções personalizadas",
      features: [
        "Clientes ilimitados",
        "Todas as funcionalidades",
        "Múltiplas filiais",
        "ChatBot integrado",
        "Integração com e-commerce",
        "Integração com sistemas externos",
        "API personalizada",
        "Suporte 24/7",
        "Gerente de conta dedicado"
      ],
      highlighted: false
    }
  ];

  const [openFaq, setOpenFaq] = useState(null);

  const faqData = [
    {
      question: "Como funciona o sistema de pontos?",
      answer: "O sistema é simples: você define quantos pontos o cliente ganha por real gasto (ex: 1 ponto por R$ 1,00). Os clientes acumulam pontos a cada compra e podem trocá-los por prêmios do seu catálogo. Tudo é automático e você acompanha em tempo real."
    },
    {
      question: "Posso personalizar o programa de acordo com minha empresa?",
      answer: "Sim! O MeuFiel é totalmente personalizável. Você pode definir suas próprias regras de pontuação, criar seu catálogo de prêmios, personalizar cores e logo, configurar bônus especiais e muito mais. O sistema se adapta ao seu negócio."
    },
    {
      question: "Como os clientes consultam seus pontos?",
      answer: "Os clientes podem consultar seus pontos de forma segura através da área do cliente, fazendo login com e-mail e senha. Também enviamos SMS automáticos sempre que ganham ou gastam pontos."
    },
    {
      question: "O sistema funciona para qualquer tipo de negócio?",
      answer: "Sim! O MeuFiel foi desenvolvido para funcionar em qualquer segmento: restaurantes, lojas, salões de beleza, farmácias, pet shops, academias e muito mais. A flexibilidade do sistema permite adaptar às necessidades específicas de cada negócio."
    },
    {
      question: "Como faço para cadastrar meus clientes?",
      answer: "Existem duas formas: você pode cadastrar manualmente através do sistema (ideal para estabelecimentos físicos) ou disponibilizar um QR Code para que os próprios clientes se cadastrem (ótimo para acelerar o processo e reduzir filas)."
    },
    {
      question: "Preciso de conhecimento técnico para usar?",
      answer: "Não! O MeuFiel foi desenvolvido para ser extremamente fácil de usar. A interface é intuitiva e oferecemos suporte completo para configuração inicial. Em poucos minutos você estará operando seu programa de fidelidade."
    },
    {
      question: "Como funciona o período de teste?",
      answer: "Oferecemos 5 dias de teste grátis com acesso completo a todas as funcionalidades. Você pode cadastrar clientes, configurar prêmios, testar notificações SMS e explorar todos os recursos sem compromisso."
    },
    {
      question: "Posso cancelar quando quiser?",
      answer: "Sim, não há fidelidade. Você pode cancelar seu plano a qualquer momento através do painel administrativo ou entrando em contato conosco. Seus dados ficam preservados caso queira reativar no futuro."
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <img src="logo1200.png" alt="MeuFiel Logo" className="w-8 h-8 mr-3" />
              <span className="text-2xl font-bold text-gray-900">MeuFiel</span>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex space-x-8">
              <a href="#target-audience" className="text-gray-600 hover:text-blue-600 transition-colors">
                Para Quem é
              </a>
              <a href="#how-it-works" className="text-gray-600 hover:text-blue-600 transition-colors">
                Como Funciona
              </a>
              <a href="#benefits" className="text-gray-600 hover:text-blue-600 transition-colors">
                Benefícios
              </a>
              <a href="#pricing" className="text-gray-600 hover:text-blue-600 transition-colors">
                Preços
              </a>
              <a href="#faq" className="text-gray-600 hover:text-blue-600 transition-colors">
                FAQ
              </a>
            </nav>
            
            {/* Desktop Buttons */}
            <div className="hidden lg:flex items-center space-x-4">
              <Link
                to="/customer-auth"
                className="text-blue-600 hover:text-blue-700 font-medium transition-colors whitespace-nowrap"
              >
                Consulte seus Pontos
              </Link>
              <Link
                to="/trial-registration"
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap"
              >
                Teste 5 dias Grátis
              </Link>
              <Link
                to="/login"
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                Acesso Lojista
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsMobileMenuOpen(!isMobileMenuOpen);
              }}
              className="lg:hidden p-2 rounded-md text-gray-600 hover:text-blue-600 hover:bg-gray-100 transition-colors"
              aria-label="Toggle mobile menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white border-b shadow-lg z-50">
            <div className="px-4 py-4 space-y-4">
              {/* Mobile Navigation Links */}
              <div className="border-b pb-4 space-y-3">
                <a 
                  href="#target-audience" 
                  className="block text-gray-600 hover:text-blue-600 transition-colors py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Para Quem é
                </a>
                <a 
                  href="#how-it-works" 
                  className="block text-gray-600 hover:text-blue-600 transition-colors py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Como Funciona
                </a>
                <a 
                  href="#benefits" 
                  className="block text-gray-600 hover:text-blue-600 transition-colors py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Benefícios
                </a>
                <a 
                  href="#pricing" 
                  className="block text-gray-600 hover:text-blue-600 transition-colors py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Preços
                </a>
                <a 
                  href="#faq" 
                  className="block text-gray-600 hover:text-blue-600 transition-colors py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  FAQ
                </a>
              </div>
              
              {/* Mobile Action Buttons */}
              <div className="space-y-3">
                <Link
                  to="/customer-auth"
                  className="block text-center text-blue-600 hover:text-blue-700 font-medium transition-colors py-3 border border-blue-600 rounded-lg hover:bg-blue-50"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Consulte seus Pontos
                </Link>
                <Link
                  to="/trial-registration"
                  className="block text-center bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Teste 5 dias Grátis
                </Link>
                <Link
                  to="/login"
                  className="block text-center bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Acesso Lojista
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Hero Carousel Section */}
      <section 
        className="relative overflow-hidden"
        onMouseEnter={() => setIsCarouselPaused(true)}
        onMouseLeave={() => setIsCarouselPaused(false)}
      >
        <div className="relative h-[600px] md:h-[700px]">
          {carouselSlides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-transform duration-500 ease-in-out ${
                index === currentSlide ? 'translate-x-0' : 
                index < currentSlide ? '-translate-x-full' : 'translate-x-full'
              }`}
            >
              <div 
                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${slide.image})` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/30"></div>
              </div>
              
              <div className="relative z-10 h-full flex items-center">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                  <div className="max-w-4xl">
                    <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight drop-shadow-2xl">
                      {slide.title}
                      <span className="text-blue-400 block">{slide.subtitle}</span>
                    </h1>
                    <p className="text-lg md:text-xl lg:text-2xl text-gray-100 mb-6 max-w-3xl leading-relaxed drop-shadow-lg bg-black/20 p-4 rounded-lg backdrop-blur-sm">
                      {slide.description}
                    </p>
                    <div className="mb-8">
                      <div className="inline-flex items-center bg-gradient-to-r from-indigo-500 to-purple-600 backdrop-blur-sm text-white px-6 py-3 rounded-full text-lg font-semibold drop-shadow-lg border border-indigo-400/30">
                        <span className="mr-2">
                          {slide.highlight.icon}
                        </span>
                        {slide.highlight.text}
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                      {slide.buttons.map((button, btnIndex) => (
                        button.external ? (
                          <a
                            key={btnIndex}
                            href={button.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center justify-center drop-shadow-lg ${
                              button.primary
                                ? 'bg-blue-600/90 backdrop-blur-sm text-white hover:bg-blue-700/90 border border-blue-400/30'
                                : 'bg-white/90 backdrop-blur-sm text-blue-600 border-2 border-white/50 hover:bg-white'
                            }`}
                          >
                            {button.text.includes('Demonstração') && <Play className="mr-2 w-5 h-5" />}
                            {button.text}
                            <ArrowRight className="ml-2 w-5 h-5" />
                          </a>
                        ) : (
                          <Link
                            key={btnIndex}
                            to={button.link}
                            className={`px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center justify-center drop-shadow-lg ${
                              button.primary
                                ? 'bg-blue-600/90 backdrop-blur-sm text-white hover:bg-blue-700/90 border border-blue-400/30'
                                : 'bg-white/90 backdrop-blur-sm text-blue-600 border-2 border-white/50 hover:bg-white'
                            }`}
                          >
                            {button.text}
                            <ArrowRight className="ml-2 w-5 h-5" />
                          </Link>
                        )
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Carousel Controls */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/30 hover:bg-black/50 backdrop-blur-sm text-white p-3 rounded-full transition-all z-20 border border-white/20"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/30 hover:bg-black/50 backdrop-blur-sm text-white p-3 rounded-full transition-all z-20 border border-white/20"
        >
          <ChevronRight className="w-6 h-6" />
        </button>

        {/* Carousel Indicators */}
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-3 z-20">
          {carouselSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 border border-white/30 ${
                index === currentSlide ? 'bg-white scale-125' : 'bg-white/50 hover:bg-white/80'
              }`}
            />
          ))}
        </div>

        {/* Pause indicator */}
        {isCarouselPaused && (
          <div className="absolute top-6 right-6 bg-black/50 backdrop-blur-sm text-white px-3 py-2 rounded-lg text-sm font-medium z-20 border border-white/20">
            ⏸️ Pausado
          </div>
        )}
      </section>

      {/* Video Demo Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-indigo-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Entenda como fidelizar seus clientes <span className="text-blue-200">com o MeuFiel</span>
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8">
              Assista nossa demonstração completa e descubra como transformar seus clientes em defensores da sua marca com nosso sistema de fidelidade inteligente.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* Video Container */}
              <div className="relative" style={{ paddingBottom: '56.25%' /* 16:9 aspect ratio */ }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/RXR8J0tDw3I"
                  title="MeuFiel - Sistema de Fidelidade Demonstração"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              
              {/* Video Info Bar */}
              <div className="p-6 bg-gradient-to-r from-gray-50 to-gray-100">
                <div className="flex flex-col sm:flex-row items-center justify-between">
                  <div className="text-center sm:text-left mb-4 sm:mb-0">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Demonstração Completa do Sistema
                    </h3>
                    <p className="text-gray-600">
                      Veja todas as funcionalidades em apenas 5 minutos
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to="/trial-registration"
                      className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200"
                    >
                      Começar Teste Grátis
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                    <a
                      href="https://www.youtube.com/watch?v=RXR8J0tDw3I"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-6 py-3 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors duration-200"
                    >
                      <Play className="mr-2 w-4 h-4" />
                      Ver no YouTube
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center text-white">
                <Clock className="w-8 h-8 mx-auto mb-3 text-blue-200" />
                <h4 className="font-semibold mb-2">5 Minutos</h4>
                <p className="text-sm text-blue-100">Demonstração completa</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center text-white">
                <Users className="w-8 h-8 mx-auto mb-3 text-blue-200" />
                <h4 className="font-semibold mb-2">Todas as Funcionalidades</h4>
                <p className="text-sm text-blue-100">Sistema completo em ação</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center text-white">
                <Zap className="w-8 h-8 mx-auto mb-3 text-blue-200" />
                <h4 className="font-semibold mb-2">Implementação Rápida</h4>
                <p className="text-sm text-blue-100">Pronto para usar hoje</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Funcionalidades Completas
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tudo que você precisa para criar e gerenciar um programa de fidelidade de sucesso
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center p-6 rounded-xl hover:shadow-lg transition-shadow">
                <div className="text-blue-600 flex justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section id="target-audience" className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Para quem é o <span className="text-blue-600">MeuFiel?</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Nosso sistema se adapta perfeitamente a diversos tipos de negócios, 
              oferecendo soluções personalizadas para cada segmento.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {targetAudience.map((audience, index) => (
              <div key={index} className="group relative">
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-2">
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={audience.image} 
                      alt={audience.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 text-white">
                      {audience.icon}
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {audience.title}
                    </h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {audience.description}
                    </p>
                    
                    {/* Benefits List */}
                    <ul className="space-y-2">
                      {audience.benefits.map((benefit, benefitIndex) => (
                        <li key={benefitIndex} className="flex items-center text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                    
                    {/* CTA Button */}
                    <div className="mt-6">
                      <Link
                        to="/trial-registration"
                        className="block w-full text-center bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                      >
                        Teste Grátis para {audience.title.split(' ')[0]}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Como Funciona nosso <span className="text-blue-600">Sistema de Fidelidade?</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Processo simples e automatizado de fidelização que transforma visitantes em clientes fiéis. 
              Veja como funciona o ciclo completo do nosso programa de pontos e recompensas:
            </p>
          </div>

          {/* Steps Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {howItWorksSteps.map((step, index) => (
              <div key={index} className="relative">
                {/* Step Card */}
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 h-full transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
                  {/* Step Number */}
                  <div className="flex items-center mb-6">
                    <div className={`${step.color} text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg mr-4`}>
                      {step.step}
                    </div>
                    <div className={`${step.color} text-white p-3 rounded-lg`}>
                      {step.icon}
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {step.description}
                  </p>

                  {/* Details List */}
                  <ul className="space-y-2">
                    {step.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-center text-sm text-gray-700">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Arrow Connector (except for last step) */}
                {index < howItWorksSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-8 h-8 text-blue-400" />
                  </div>
                )}

                {/* Arrow Connector for mobile (downward) */}
                {index < howItWorksSteps.length - 1 && (
                  <div className="lg:hidden flex justify-center mt-4">
                    <ArrowDown className="w-8 h-8 text-blue-400" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Process Flow Visual */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                O Ciclo da Fidelização
              </h3>
              <p className="text-gray-600">
                Veja como cada etapa fortalece o relacionamento com seus clientes
              </p>
            </div>

            {/* Cycle Visual */}
            <div className="flex flex-wrap justify-center items-center gap-4">
              <div className="flex items-center bg-white rounded-lg p-4 shadow-sm">
                <UserPlus className="w-6 h-6 text-blue-500 mr-3" />
                <span className="font-medium text-gray-800">Cadastro</span>
              </div>
              <ArrowRight className="w-6 h-6 text-gray-400 hidden sm:block" />
              
              <div className="flex items-center bg-white rounded-lg p-4 shadow-sm">
                <ShoppingCart className="w-6 h-6 text-green-500 mr-3" />
                <span className="font-medium text-gray-800">Compra</span>
              </div>
              <ArrowRight className="w-6 h-6 text-gray-400 hidden sm:block" />
              
              <div className="flex items-center bg-white rounded-lg p-4 shadow-sm">
                <Star className="w-6 h-6 text-yellow-500 mr-3" />
                <span className="font-medium text-gray-800">Pontos</span>
              </div>
              <ArrowRight className="w-6 h-6 text-gray-400 hidden sm:block" />
              
              <div className="flex items-center bg-white rounded-lg p-4 shadow-sm">
                <Gift className="w-6 h-6 text-purple-500 mr-3" />
                <span className="font-medium text-gray-800">Resgate</span>
              </div>
              <ArrowRight className="w-6 h-6 text-gray-400 hidden sm:block" />
              
              <div className="flex items-center bg-white rounded-lg p-4 shadow-sm">
                <Share2 className="w-6 h-6 text-indigo-500 mr-3" />
                <span className="font-medium text-gray-800">Indicação</span>
              </div>
              <ArrowRight className="w-6 h-6 text-gray-400 hidden sm:block" />
              
              <div className="flex items-center bg-white rounded-lg p-4 shadow-sm">
                <Repeat className="w-6 h-6 text-pink-500 mr-3" />
                <span className="font-medium text-gray-800">Retorno</span>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center mt-16">
            <div className="bg-blue-600 rounded-2xl p-8 text-white">
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                Pronto para Implementar na sua Empresa?
              </h3>
              <p className="text-xl mb-6 text-blue-100">
                Comece hoje mesmo e veja seus clientes se tornarem mais fiéis!
              </p>
              <Link
                to="/trial-registration"
                className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-blue-50 transition-colors inline-flex items-center"
              >
                Iniciar Teste Grátis por 5 dias
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Por que Escolher nosso Sistema de Fidelidade?
            </h2>
            <p className="text-xl text-gray-600">
              Resultados comprovados que impulsionam seu negócio com programa de pontos profissional
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-sm text-center">
                <div className="text-blue-600 flex justify-center mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>

          <div className="bg-blue-600 rounded-2xl p-8 md:p-12 text-center text-white">
            <h3 className="text-3xl font-bold mb-4">
              Pronto para Revolucionar seu Negócio?
            </h3>
            <p className="text-xl mb-8 text-blue-100">
              Junte-se a centenas de empresas que já aumentaram suas vendas com o MeuFiel
            </p>
            <a
              href="https://wa.me/5585994190939?text=Olá! Estou interessado no MeuFiel e gostaria de saber mais sobre como implementar um programa de fidelidade na minha empresa."
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center"
            >
              Começar Agora
              <ChevronRight className="ml-2 w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Planos que Crescem com Você
            </h2>
            <p className="text-xl text-gray-600 mb-6">
              Escolha o plano ideal para o tamanho do seu negócio
            </p>
            <div className="inline-flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full font-semibold">
              <Gift className="w-4 h-4 mr-2" />
              Todos os planos incluem 5 dias de teste grátis
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`rounded-2xl p-6 ${
                  plan.highlighted
                    ? 'bg-blue-600 text-white shadow-2xl transform lg:scale-105'
                    : 'bg-white border-2 border-gray-200 text-gray-900'
                }`}
              >
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                  <p className={`mb-4 text-sm ${plan.highlighted ? 'text-blue-100' : 'text-gray-600'}`}>
                    {plan.description}
                  </p>
                  
                  {/* Preço principal (semestral destacado) */}
                  <div className="mb-3">
                    <div className="flex items-baseline justify-center">
                      <span className="text-2xl lg:text-3xl font-bold">{plan.price}</span>
                      <span className={`ml-1 text-sm ${plan.highlighted ? 'text-blue-100' : 'text-gray-600'}`}>
                        {plan.period}
                      </span>
                    </div>
                    {plan.periodType === 'semestral' && (
                      <div className={`text-xs mt-1 ${plan.highlighted ? 'text-blue-200' : 'text-blue-600'} font-medium`}>
                        💰 Plano semestral
                      </div>
                    )}
                  </div>
                  
                  {/* Preço alternativo (mensal) */}
                  {plan.originalPrice && (
                    <div className={`text-xs ${plan.highlighted ? 'text-blue-200' : 'text-gray-500'} border-t pt-2`}>
                      ou {plan.originalPrice}/mês no plano mensal
                    </div>
                  )}
                </div>
                
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <CheckCircle className={`w-4 h-4 mr-2 mt-0.5 flex-shrink-0 ${plan.highlighted ? 'text-blue-200' : 'text-blue-500'}`} />
                      <span className={`text-sm ${plan.highlighted ? 'text-blue-100' : 'text-gray-600'}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                
                <a
                  href={`https://wa.me/5585994190939?text=Olá! Tenho interesse no plano ${plan.name} do MeuFiel (${plan.price}${plan.period}). Gostaria de saber mais detalhes e como contratar.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full py-2.5 px-4 rounded-lg font-semibold text-center block transition-colors text-sm ${
                    plan.highlighted
                      ? 'bg-white text-blue-600 hover:bg-gray-100'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  Escolher Plano
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Perguntas Frequentes
            </h2>
            <p className="text-xl text-gray-600">
              Tire suas dúvidas sobre o MeuFiel
            </p>
          </div>
          
          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden"
              >
                <button
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-100 transition-colors"
                  onClick={() => toggleFaq(index)}
                >
                  <span className="text-lg font-semibold text-gray-900">
                    {faq.question}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-500 transition-transform ${
                      openFaq === index ? 'transform rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-gray-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">
              Ainda tem dúvidas? Estamos aqui para ajudar!
            </p>
            <a
              href="https://wa.me/5585994190939?text=Olá! Tenho algumas dúvidas sobre o MeuFiel e gostaria de conversar com vocês."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Falar com Especialista
              <ChevronRight className="ml-2 w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Sobre nosso Sistema de Fidelidade MeuFiel
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Somos especialistas em sistemas de fidelidade de clientes e programas de pontos. 
                Entendemos que cada cliente fiel vale muito mais que um cliente ocasional. Nossa missão 
                é ajudar empresas de todos os tamanhos a construir relacionamentos duradouros através 
                de programas de fidelização eficazes.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Com nosso software de fidelização MeuFiel, você tem acesso a uma plataforma completa 
                de gestão de clientes, fácil de usar e totalmente personalizável para criar o programa 
                de recompensas perfeito para seu negócio. Sistema ideal para qualquer segmento que 
                deseja implementar um cartão fidelidade digital.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">500+</div>
                  <div className="text-gray-600">Empresas Ativas</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">50k+</div>
                  <div className="text-gray-600">Clientes Fidelizados</div>
                </div>
              </div>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Entre em Contato
              </h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <Mail className="w-5 h-5 text-blue-600 mr-3" />
                  <span className="text-gray-600">contato.meufiel@gmail.com</span>
                </div>
                <div className="flex items-center">
                  <Phone className="w-5 h-5 text-blue-600 mr-3" />
                  <span className="text-gray-600">(85) 99419-0939</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 text-blue-600 mr-3" />
                  <span className="text-gray-600">Fortaleza, CE - Brasil</span>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-500 text-center">
                  Suporte técnico disponível de segunda a sexta, das 8h às 18h
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <Trophy className="w-8 h-8 text-blue-400 mr-3" />
                <span className="text-2xl font-bold">MeuFiel</span>
              </div>
              <p className="text-gray-400">
                A plataforma completa de fidelidade para seu negócio crescer.
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Produto</h4>
              <ul className="space-y-2">
                <li><a href="#features" className="text-gray-400 hover:text-white transition-colors">Funcionalidades</a></li>
                <li><a href="#pricing" className="text-gray-400 hover:text-white transition-colors">Preços</a></li>
                <li><Link to="/customer-auth" className="text-gray-400 hover:text-white transition-colors">Área do Cliente</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Empresa</h4>
              <ul className="space-y-2">
                <li><a href="#about" className="text-gray-400 hover:text-white transition-colors">Sobre Nós</a></li>
                <li><Link to="/login" className="text-gray-400 hover:text-white transition-colors">Acesso Lojista</Link></li>
                <li><a href="mailto:contato@meufiel.com" className="text-gray-400 hover:text-white transition-colors">Suporte</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Contato</h4>
              <ul className="space-y-2">
                <li className="text-gray-400">contato@meufiel.com</li>
                <li className="text-gray-400">(85) 99419-0939</li>
                <li className="text-gray-400">Fortaleza, CE</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <p className="text-gray-400">
              © 2025 MeuFiel. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
